import React, { createContext, createRef, useState, useMemo, useCallback } from 'react';

import { FuelButton, FuelToast } from '@frete.com/fuel-react';

import * as Styled from './styled';
import { IToast, IToastContext, IToastProviderContext } from './types';

export const ToastContext = createContext({} as IToastContext);

export const MAX_TOAST_BY_PAGE = 3;
export const TOAST_TIMING_DEFAULT = 30_000;

// eslint-disable-next-line no-promise-executor-return
const sleep = (time: number) => new Promise((resolve) => setTimeout(resolve, time));

export const ToastProvider = ({ children }: IToastProviderContext) => {
  const [openToasts, setOpenToasts] = useState<IToast[]>([]);
  const memoOpenToasts = useMemo(() => openToasts, [openToasts]);

  const addOpenToasts = useCallback(
    (newToast: IToast) => setOpenToasts((previousState) => [...previousState, newToast]),
    [memoOpenToasts]
  );
  const removeOpenToasts = (toastToRemove: IToast) =>
    setOpenToasts(memoOpenToasts.filter((toast) => toast !== toastToRemove));

  const randomId = () => window.crypto.getRandomValues(new Uint32Array(1))[0].toString(36);

  async function createToast(toast: IToast) {
    const toastRef = createRef<HTMLFuelToastElement>();
    toast.ref = toastRef;
    toast.id = randomId();
    if (openToasts.length >= MAX_TOAST_BY_PAGE) {
      removeOpenToasts(openToasts[0]);
      openToasts.shift();
    }

    addOpenToasts(toast);

    await sleep(100);

    await toastRef.current?.open();
    await sleep(toast.timing || TOAST_TIMING_DEFAULT);
    await toastRef.current?.close();
  }

  const contextValue = useMemo(() => ({ createToast }), [createToast]);
  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <Styled.ToastWrapper>
        {memoOpenToasts.map((toast) => {
          const isOnClickWorking = !toast.primaryButton && !toast.secondaryButton;
          return (
            <FuelToast
              data-testid={toast.id}
              key={toast.id}
              ref={toast.ref}
              severity={toast.severity}
              heading={toast.heading}
              message={toast.message}
              onClick={() => isOnClickWorking && removeOpenToasts(toast)}
            >
              {!!toast.primaryButton && (
                <FuelButton slot="button" {...toast.primaryButton}>
                  {toast.primaryButton?.children}
                </FuelButton>
              )}
              {!!toast.secondaryButton && (
                <FuelButton slot="button" {...toast.secondaryButton}>
                  {toast.secondaryButton?.children}
                </FuelButton>
              )}
            </FuelToast>
          );
        })}
      </Styled.ToastWrapper>
    </ToastContext.Provider>
  );
};
